import { createClient } from '@/lib/supabase/client';
import { InterviewStatus } from './InterviewTypes';
import { getInterviewQuestions } from './InterviewUtils';
import { v4 as uuidv4 } from 'uuid';

export { getInterviewQuestions };

const supabase = createClient();

export async function checkAuth() {
  const {
    data: { session },
    error
  } = await supabase.auth.getSession();

  if (error) {
    console.error('Error checking auth:', error);
    throw new Error('Authentication error');
  }

  if (!session) {
    throw new Error('No active session');
  }

  return session;
}

export async function getCandidateId(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('candidates')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    // PGRST116 is "no rows found"
    console.error('Error fetching candidate data:', error);
    throw new Error('Failed to fetch candidate data');
  }

  return data?.id || null;
}

export async function getInterviewStatus(
  interviewTakenId: string
): Promise<InterviewStatus | null> {
  const { data, error } = await supabase
    .from('interviews_taken')
    .select('status')
    .eq('id', interviewTakenId)
    .maybeSingle();

  // if (error && error.code !== 'PGRST116') {
  //   console.error('Error fetching interview status:', error);
  //   throw new Error('Failed to fetch interview status');
  // }

  // console.log('Interview status from database:', data?.status);
  return (data?.status as InterviewStatus) || null;
}

export async function updateInterviewStatus(
  interviewTakenId: string,
  conversationId?: string,
  transcript?: any[] | string,
  summary?: string
) {
  const updateData: Record<string, unknown> = {};

  if (conversationId) {
    updateData.conversation_id = conversationId;
  }

  if (transcript) {
    updateData.status = 'coding';
    updateData.transcript = transcript;
  }

  if (summary) {
    updateData.summary = summary;
  }

  const { error } = await supabase
    .from('interviews_taken')
    .update(updateData)
    .eq('id', interviewTakenId);

  if (error) {
    console.error('Error updating interview status:', error);
    throw new Error('Failed to update interview status');
  }
}

export async function updateInterviewStatusWrapUp(
  interviewTakenId: string,
  transcript?: any[] | string
) {
  const updateData: Record<string, unknown> = {};

  if (transcript) {
    updateData.status = 'finished';
    updateData.wrap_up_transcript = transcript;
  }

  const { error } = await supabase
    .from('interviews_taken')
    .update(updateData)
    .eq('id', interviewTakenId);

  if (error) {
    console.error('Error updating interview status:', error);
    throw new Error('Failed to update interview status');
  }
}

export async function fetchConversationDetails(conversationId: string) {
  const response = await fetch(
    `/api/elevenlabs/conversation/${conversationId}`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch conversation details');
  }

  return await response.json();
}

// /**
//  * Unified function to check interview status and handle redirects
//  * @param interviewTakenId The ID of the interview to check
//  * @param expectedStatus The expected status for the current phase
//  * @param router Next.js router for redirects
//  * @param onStatusMatch Callback if the status matches expected status
//  * @returns The interview status, or null if not found
//  */
// export async function checkInterviewPhase(
//   interviewTakenId: string,
//   expectedStatus: InterviewStatus | InterviewStatus[],
//   router: AppRouterInstance,
//   onStatusMatch?: (status: InterviewStatus) => void
// ): Promise<InterviewStatus | null> {
//   try {
//     const status = await getInterviewStatus(interviewTakenId);

//     if (!status) {
//       console.log('No interview status found for ID:', interviewTakenId);
//       return null;
//     }

//     // console.log('Found interview with status:', status);

//     // Check if status matches expected status(es)
//     const expectedStatuses = Array.isArray(expectedStatus)
//       ? expectedStatus
//       : [expectedStatus];
//     const isExpectedStatus = expectedStatuses.includes(status);

//     if (isExpectedStatus) {
//       console.log(`Status "${status}" matches expected status`);

//       if (onStatusMatch) {
//         onStatusMatch(status);
//       }

//       return status;
//     }

//     // Handle redirects based on status
//     console.log(
//       `Status "${status}" doesn't match expected status, redirecting...`
//     );

//     switch (status) {
//       case 'initial':
//         router.push(`/candidate/interviews/${interviewTakenId}`);
//         break;

//       case 'coding':
//         router.push(`/candidate/interviews/${interviewTakenId}`);
//         break;

//       case 'wrap_up':
//         router.push(`/candidate/interviews/${interviewTakenId}`);
//         break;

//       case 'finished':
//         router.push('/candidate/interviews');
//         break;

//       default:
//         console.error('Unknown interview status:', status);
//         router.push('/candidate/interviews');
//     }

//     return status;
//   } catch (error) {
//     console.error('Error checking interview phase:', error);
//     return null;
//   }
// }

/**
 * Creates a new interview_taken record for a candidate
 * @param candidateId The ID of the candidate taking the interview
 * @param interviewId The ID of the interview template being taken
 * @returns The ID of the newly created interview_taken record
 */
export async function createNewInterviewTaken(
  candidateId: string,
  interviewId: string
): Promise<string> {
  // Generate a UUID for the new record
  const newInterviewTakenId = uuidv4();

  const { error } = await supabase.from('interviews_taken').insert({
    id: newInterviewTakenId,
    interview_id: interviewId,
    candidate_id: candidateId,
    status: 'initial'
  });

  if (error) {
    console.error('Error creating new interview_taken record:', error);
    throw new Error('Failed to create new interview. Please try again.');
  }

  return newInterviewTakenId;
}

/**
 * Updates an interview status to transition to the coding phase
 * @param interviewTakenId The ID of the interview to transition
 * @returns Promise that resolves when the update is complete
 */
export async function transitionToCodingPhase(
  interviewTakenId: string
): Promise<void> {
  try {
    // Update the interview status to 'coding'
    const result = await supabase
      .from('interviews_taken')
      .update({
        status: 'coding'
      })
      .eq('id', interviewTakenId);

    if (result.error) {
      console.error('Error updating interview status:', result.error);
      throw new Error('Failed to update interview status');
    }

    console.log(
      'Interview successfully transitioned to coding phase:',
      interviewTakenId
    );
  } catch (error) {
    console.error('Error transitioning to coding phase:', error);
    throw new Error('Failed to transition to coding phase');
  }
}
